import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import { Badge } from '@/components/ui/Badge/Badge';
import { ContentPasteSearchIcon } from '@/components/ui/Icons';
import { api } from '@/api/client';
import type { Author } from '@/api/client';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { useCatalogManageOpener, useCanManageCatalog } from '@/contexts/CatalogManageOpenerContext';
import { ComboEmptyCreatePanel } from '@/components/ui/ComboEmptyCreatePanel/ComboEmptyCreatePanel';
import './AuthorCombo.css';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 25;
const MIN_SEARCH_LENGTH = 2;

export type AuthorValueItem = { id: string; name: string };

interface AuthorComboProps {
  value: AuthorValueItem[];
  onChange: (authors: AuthorValueItem[]) => void;
  placeholder?: string;
  disabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  debounceFlushKey?: string | number;
}

type Option = Author | { id: string; name: string };

export function AuthorCombo({
  value,
  onChange,
  placeholder = 'Автор',
  disabled,
  containerRef,
  debounceFlushKey,
}: AuthorComboProps) {
  const openCatalogManage = useCatalogManageOpener();
  const canManageCatalog = useCanManageCatalog();
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [accumulatedItems, setAccumulatedItems] = useState<Option[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const containerRefLocal = useRef<HTMLDivElement>(null);
  const dropdownWrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLElement | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const resolvedContainerRef = containerRef ?? containerRefLocal;
  const dropdownStyle = useDropdownPosition(resolvedContainerRef, open, { dropdownRef: dropdownWrapRef });

  const searchQuery = inputValue.trim();
  const debouncedSearch = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS, debounceFlushKey);
  const { data: searchResponse, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['authors', debouncedSearch, page],
    queryFn: () => api.authors.list(debouncedSearch, { limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    enabled: debouncedSearch.length >= MIN_SEARCH_LENGTH,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!searchResponse || isPlaceholderData) return;
    if (page === 0) {
      setAccumulatedItems(searchResponse.items);
      setHasMore(searchResponse.hasMore);
    } else {
      setAccumulatedItems((prev) => [...prev, ...searchResponse.items]);
      setHasMore(searchResponse.hasMore);
    }
  }, [searchResponse, page, debouncedSearch, isPlaceholderData]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const valueIds = value.map((r) => r.id);
  const filtered = accumulatedItems.filter((r) => !valueIds.includes(r.id));
  const options: Option[] = filtered;

  const handleLoadMore = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPage((p) => p + 1);
  }, []);

  const selectOption = useCallback(
    (opt: Option) => {
      onChange([...value, { id: opt.id, name: opt.name }]);
      setInputValue('');
      setOpen(false);
    },
    [value, onChange]
  );

  const openCreateAuthor = useCallback(() => {
    const q = searchQuery;
    setInputValue('');
    setOpen(false);
    openCatalogManage({
      tab: 'persons',
      rolePreset: 'author',
      nameSeed: q,
      onPersonCreated: (payload) => {
        const current = valueRef.current;
        const ids = new Set(current.map((a) => a.id));
        const next = [...current];
        for (const a of payload.authors) {
          if (!ids.has(a.id)) {
            next.push({ id: a.id, name: a.name });
            ids.add(a.id);
          }
        }
        if (next.length !== current.length) onChange(next);
      },
    });
  }, [searchQuery, openCatalogManage, onChange]);

  const removeAuthor = (item: AuthorValueItem) => {
    onChange(value.filter((r) => r.id !== item.id));
  };

  useEffect(() => {
    setHighlightIndex(0);
  }, [options.length]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[highlightIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if ((e.key === 'ArrowDown' || e.key === 'Enter') && searchQuery.length >= MIN_SEARCH_LENGTH) setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[highlightIndex];
      if (opt) selectOption(opt);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleBlur = () => {
    setOpen(false);
  };

  return (
    <div ref={resolvedContainerRef as React.RefObject<HTMLDivElement>} className="author-combo">
      <div className={`author-combo__field ${value.length > 0 && !disabled ? 'author-combo__field_has-clear' : ''}`}>
        <div className="author-combo__chips">
          {value.map((r) => (
            <Badge key={r.id} variant="author">
              {r.name}
              {!disabled && (
                <button
                  type="button"
                  className="badge__remove"
                  onClick={() => removeAuthor(r)}
                  aria-label={`Удалить ${r.name}`}
                >
                  ×
                </button>
              )}
            </Badge>
          ))}
        </div>
        <div className="author-combo__input-wrap">
          <input
          className={`author-combo__input ${isFetching ? 'author-combo__input_loading' : ''}`}
          value={inputValue}
          onChange={(e) => {
            const v = e.target.value;
            setInputValue(v);
            setOpen(v.trim().length > 0);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? '' : placeholder}
          disabled={disabled}
          autoComplete="off"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls="author-combo-list"
          aria-activedescendant={open ? `author-opt-${highlightIndex}` : undefined}
        />
        {isFetching && <span className="author-combo__loading">…</span>}
        </div>
        {value.length > 0 && !disabled && (
          <ComboClearButton onClick={() => onChange([])} />
        )}
      </div>
      {open && searchQuery.length > 0 &&
        createPortal(
          <div
            ref={dropdownWrapRef}
            className="author-combo__dropdown-wrap"
            data-combo-dropdown
            style={dropdownStyle}
          >
            {searchQuery.length < MIN_SEARCH_LENGTH ? (
              <div className="author-combo__hint">Введите минимум 2 символа для поиска</div>
            ) : options.length === 0 && isFetching ? (
              <div className="author-combo__hint">Загрузка…</div>
            ) : options.length === 0 ? (
              <ComboEmptyCreatePanel
                actions={
                  canManageCatalog
                    ? [
                        {
                          id: '__create_author',
                          label: 'Создать автора',
                          onSelect: openCreateAuthor,
                        },
                      ]
                    : []
                }
              />
            ) : (
            <>
            <OverlayScrollArea
              contentRef={listRef}
              element="ul"
              id="author-combo-list"
              className="author-combo__dropdown"
              role="listbox"
            >
              {options.map((opt, i) => (
                <li
                  key={opt.id}
                  id={`author-opt-${i}`}
                  role="option"
                  aria-selected={i === highlightIndex}
                  className={`author-combo__option ${i === highlightIndex ? 'author-combo__option_highlight' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectOption(opt);
                  }}
                >
                  {opt.name}
                </li>
              ))}
            </OverlayScrollArea>
            {hasMore && (
              <button
                type="button"
                className="author-combo__load-more"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleLoadMore(e);
                }}
              >
                <ContentPasteSearchIcon size={18} />
                Загрузить ещё
              </button>
            )}
            </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
